import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  PlusCircle, 
  LogOut,
  Users,
  Building2,
  Menu,
  X,
  BarChart3,
  Briefcase,
  Home,
  Activity,
  Receipt,
  Gift,
  Bell,
  CheckCircle,
  AlertCircle,
  Clock,
  FileCheck,
  Check,
  Calendar,
  FolderPlus,
  Map
} from 'lucide-react';

// Notification Bell Component
const NotificationBell = ({ authFetch }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Fetch notifications - using useCallback to avoid dependency issues
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/portal/notifications');
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  // Fetch on mount and periodically
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark single notification as read
  const markAsRead = async (notificationId, jobId) => {
    try {
      await authFetch(`/api/portal/notifications/mark-read/${notificationId}`, { method: 'POST' });
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await authFetch('/api/portal/notifications/mark-all-read', { method: 'POST' });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Get icon for notification type
  const getIcon = (type) => {
    switch (type) {
      case 'new_job':
        return <FolderPlus className="w-4 h-4 text-blue-500" />;
      case 'job_served':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'job_canceled':
        return <X className="w-4 h-4 text-red-500" />;
      case 'attempt':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'attempt_success':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'invoice':
        return <Receipt className="w-4 h-4 text-blue-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  // Format relative time
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-slate-200 transition-colors"
        data-testid="notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-[#ED1C24] rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1 text-xs text-[#ED1C24] hover:text-red-700 font-medium"
                data-testid="mark-all-read"
              >
                <Check className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No notifications</p>
                <p className="text-xs text-slate-400 mt-1">You&apos;re all caught up!</p>
              </div>
            ) : (
              notifications.map((notification) => {
                // All notifications link to the job detail page
                const linkTo = `/portal/jobs/${notification.job_id}`;
                
                return (
                <Link
                  key={notification.id}
                  to={linkTo}
                  onClick={() => {
                    if (!notification.read) {
                      markAsRead(notification.id, notification.job_id);
                    }
                    setIsOpen(false);
                  }}
                  className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0 ${
                    !notification.read ? 'bg-blue-50/50' : ''
                  }`}
                  data-testid={`notification-${notification.id}`}
                >
                  <div className="mt-0.5">{getIcon(notification.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notification.read ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                      {notification.title}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{notification.description}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{formatTime(notification.timestamp)}</p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 rounded-full bg-[#ED1C24] mt-2 shrink-0" />
                  )}
                </Link>
              );
              })
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-2 bg-slate-50 border-t border-slate-200">
              <Link
                to="/portal/activity"
                onClick={() => setIsOpen(false)}
                className="text-xs text-[#ED1C24] hover:text-red-700 font-medium"
              >
                View all activity →
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Admin Notification Bell Component
const AdminNotificationBell = ({ authFetch }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch('/api/admin/notifications');
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unread_count || 0);
      }
    } catch (err) {
      console.error('Failed to fetch admin notifications:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  // Fetch on mount and periodically
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Mark single notification as read
  const markAsRead = async (notificationId, link) => {
    try {
      await authFetch(`/api/admin/notifications/mark-read/${notificationId}`, { method: 'POST' });
      setNotifications(prev => prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await authFetch('/api/admin/notifications/mark-all-read', { method: 'POST' });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Get icon for notification type
  const getIcon = (type) => {
    switch (type) {
      case 'new_client':
        return <Users className="w-4 h-4 text-blue-500" />;
      case 'new_booking':
        return <Calendar className="w-4 h-4 text-emerald-500" />;
      case 'booking_cancelled':
        return <X className="w-4 h-4 text-red-500" />;
      case 'booking_rescheduled':
        return <Clock className="w-4 h-4 text-amber-500" />;
      case 'payment_received':
        return <Receipt className="w-4 h-4 text-green-500" />;
      case 'new_job':
        return <FileCheck className="w-4 h-4 text-purple-500" />;
      default:
        return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  // Format relative time
  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg hover:bg-slate-200 transition-colors"
        data-testid="admin-notification-bell"
      >
        <Bell className="w-5 h-5 text-slate-600" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-[#ED1C24] rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b border-slate-200">
            <h3 className="font-semibold text-slate-900">Admin Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="flex items-center gap-1 text-xs text-[#ED1C24] hover:text-red-700 font-medium"
                data-testid="admin-mark-all-read"
              >
                <Check className="w-3 h-3" />
                Mark all read
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="p-4 text-center text-sm text-slate-500">Loading...</div>
            ) : notifications.length === 0 ? (
              <div className="p-6 text-center">
                <Bell className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No notifications</p>
                <p className="text-xs text-slate-400 mt-1">You&apos;re all caught up!</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => {
                    if (!notification.read) {
                      markAsRead(notification.id, notification.link);
                    }
                    if (notification.link) {
                      navigate(notification.link);
                    }
                    setIsOpen(false);
                  }}
                  className={`flex items-start gap-3 px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-100 last:border-0 cursor-pointer ${
                    !notification.read ? 'bg-blue-50/50' : ''
                  }`}
                  data-testid={`admin-notification-${notification.id}`}
                >
                  <div className="mt-0.5">{getIcon(notification.type)}</div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notification.read ? 'font-semibold text-slate-900' : 'font-medium text-slate-700'}`}>
                      {notification.title}
                    </p>
                    <p className="text-xs text-slate-500 truncate">{notification.description}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{formatTime(notification.timestamp)}</p>
                  </div>
                  {!notification.read && (
                    <div className="w-2 h-2 rounded-full bg-[#ED1C24] mt-2 shrink-0" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// NavItem component - defined outside PortalLayout to avoid recreation on each render
const NavItem = ({ item, mobile = false, isActive, onMobileClick }) => {
  const active = isActive(item.href);
  return (
    <Link
      to={item.href}
      onClick={() => mobile && onMobileClick()}
      data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
        active
          ? 'bg-[#ED1C24] text-white shadow-lg shadow-red-900/20'
          : 'text-gray-400 hover:bg-gray-800 hover:text-white'
      }`}
    >
      <item.icon className={`w-5 h-5 ${active ? 'text-white' : 'text-gray-500'}`} />
      {item.name}
    </Link>
  );
};

const PortalLayout = ({ children }) => {
  const { user, isAdmin, logout, authFetch } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  // Set page title for portal pages
  useEffect(() => {
    document.title = 'WesTex Legal Portal';
    return () => {
      // Reset title when leaving portal (optional)
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/portal/login');
  };

  // Navigation items for regular users (no Invoices - shown in job detail)
  const userNavItems = [
    { name: 'Dashboard', href: '/portal', icon: LayoutDashboard },
    { name: 'My Jobs', href: '/portal/jobs', icon: FileText },
    { name: 'Activity', href: '/portal/activity', icon: Activity },
    { name: 'Submit New Job', href: '/portal/submit', icon: PlusCircle },
  ];

  // Admin-only navigation items
  const adminNavItems = [
    { name: 'Admin Dashboard', href: '/portal/admin', icon: BarChart3 },
    { name: 'All Jobs', href: '/portal/admin/jobs', icon: Briefcase },
    { name: 'Jobs Map', href: '/portal/admin/jobs-map', icon: Map },
    { name: 'Invoices', href: '/portal/admin/invoices', icon: Receipt },
    { name: 'Notary Bookings', href: '/portal/admin/notary', icon: Calendar },
    { name: 'Referrals', href: '/portal/admin/referrals', icon: Gift },
    { name: 'Manage Clients', href: '/portal/admin/clients', icon: Users },
    { name: 'Approved Domains', href: '/portal/admin/domains', icon: Building2 },
  ];

  const isActive = (href) => {
    if (href === '/portal') {
      return location.pathname === '/portal' || location.pathname === '/portal/dashboard';
    }
    if (href === '/portal/admin') {
      return location.pathname === '/portal/admin';
    }
    return location.pathname === href || location.pathname.startsWith(href + '/');
  };

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/60 lg:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar - Dark Theme */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-[#111827] transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Portal Header */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-gray-800">
            <div className="flex items-center gap-2">
              <div className="p-1.5 bg-[#ED1C24] rounded-lg">
                <LayoutDashboard className="w-5 h-5 text-white" />
              </div>
              <span className="text-lg font-semibold text-white">Client Portal</span>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1.5 rounded-lg hover:bg-gray-800 transition-colors"
              data-testid="close-sidebar"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Back to Website Link */}
          <div className="px-3 pt-4 pb-2">
            <Link 
              to="/"
              className="flex items-center gap-2 px-3 py-2 text-xs font-medium text-gray-500 hover:text-gray-300 transition-colors"
              data-testid="back-to-website"
            >
              <Home className="w-4 h-4" />
              Back to Website
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-2 space-y-1 overflow-y-auto">
            {!isAdmin && (
              <>
                <div className="pb-2">
                  <p className="px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Portal
                  </p>
                </div>
                {userNavItems.map(item => (
                  <NavItem key={item.href} item={item} mobile isActive={isActive} onMobileClick={closeSidebar} />
                ))}
              </>
            )}
            
            {isAdmin && (
              <>
                <div className="pb-2">
                  <p className="px-3 text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    Admin
                  </p>
                </div>
                {adminNavItems.map(item => (
                  <NavItem key={item.href} item={item} mobile isActive={isActive} onMobileClick={closeSidebar} />
                ))}
              </>
            )}
          </nav>

          {/* User info & logout */}
          <div className="p-4 border-t border-gray-800">
            <div className="mb-3 px-1">
              <p className="text-sm font-medium text-white truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
              {user?.company_name && (
                <p className="text-xs text-gray-600 truncate mt-0.5">{user?.company_name}</p>
              )}
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              className="flex items-center gap-2 w-full px-3 py-2.5 text-sm font-medium text-gray-400 rounded-lg hover:bg-gray-800 hover:text-white transition-all duration-200"
            >
              <LogOut className="w-5 h-5" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 h-16 bg-slate-100 border-b border-slate-200">
          <div className="flex items-center justify-between h-full px-4 md:px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="open-sidebar"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
            
            <div className="flex items-center gap-4 ml-auto">
              {/* Notification Bell - Different for admin vs client */}
              {!isAdmin && <NotificationBell authFetch={authFetch} />}
              {isAdmin && <AdminNotificationBell authFetch={authFetch} />}
              
              {isAdmin && (
                <span className="px-3 py-1 text-xs font-semibold text-[#ED1C24] bg-red-50 rounded-full border border-red-100">
                  Admin
                </span>
              )}
              <div className="hidden sm:block text-right">
                <p className="text-sm font-medium text-gray-900">{user?.company_name}</p>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 md:p-6 lg:p-8">
          {children}
        </main>
      </div>
    </div>
  );
};

export default PortalLayout;
